import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { CustomerForm, type CustomerSaveIntent } from '../components/customers/customer-form.js';
import { CustomerOrders } from '../components/customers/customer-orders.js';
import {
  DraftMeasurementsPanel,
  type MeasurementDraft,
} from '../components/measurements/draft-measurements-panel.js';
import { MeasurementsPanel } from '../components/measurements/measurements-panel.js';
import { PageHeader } from '../components/layout/page-header.js';
import { ListSkeleton } from '@tailoring-crm/ui';
import { useCustomer } from '../lib/customers/use-customers.js';
import type { Customer } from '@tailoring-crm/shared';

/**
 * The whole customer record on one page — the screen a shop adds somebody on, and the screen it
 * corrects them on.
 *
 * A PAGE RATHER THAN THE DIALOG THIS USED TO BE, for the reason the customer's own page is one:
 * the record has outgrown a modal. A name, a number and an address fit in a box over the list;
 * a photograph, a measurement history and a year of orders do not, and stacking them into a
 * scrolling dialog would make the counter scroll past the two fields it actually came for.
 *
 * The dialog has not gone away — `CustomerFormDialog` is still what the booking form opens when
 * a walk-in turns out not to be in the book — and both are shells around one `CustomerForm`, so
 * neither can drift into asking for something the other does not.
 *
 * ON CREATE THE PAGE IS THE FORM AND THE TAPE. Orders belong to a customer who exists, and
 * their empty state beside a form nobody has submitted would be a panel apologising for a person
 * who has not been created yet. Measurements are the exception, and the reason is the counter:
 * the person being measured is standing there NOW, and telling the shop to save her first and
 * fetch the tape again afterwards is how a tailoring CRM loses to a paper diary. So the create
 * screen takes them, holds them, and posts them with her — see `createCustomerWithMeasurements`
 * in the API, which writes the customer and her measurements in one transaction.
 */
export function CustomerFormPage(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { customerId } = useParams<{ customerId: string }>();
  // What the book's search box held when somebody gave up looking and pressed Add. A number
  // becomes the mobile number, anything else becomes the name — so the form opens half-filled.
  const [urlParams] = useSearchParams();
  const prefill = urlParams.get('prefill') ?? '';

  // `useCustomer(null)` is the create case and asks for nothing.
  const customerQuery = useCustomer(customerId ?? null);
  const isEdit = Boolean(customerId);
  const customer = customerQuery.data ?? null;

  /**
   * Measurements taken before there is anybody to hang them on. Held here rather than inside
   * `CustomerForm` because the form is also the booking flow's dialog, which asks for a name and
   * a number and must not grow a tape measure.
   */
  const [drafts, setDrafts] = useState<MeasurementDraft[]>([]);

  /**
   * Where saving lands.
   *
   * `save-and-order` goes to booking with the customer already chosen, which is what a shop
   * adding somebody at the counter is nearly always about to do next. A plain save goes to the
   * customer's own page — the record it just wrote, not back to the list, so the person can see
   * that what they typed is what was stored.
   */
  const afterSave = (saved: Customer, intent: CustomerSaveIntent): void => {
    if (intent === 'save-and-order') {
      navigate(`/orders/new?customerId=${saved.id}`);
      return;
    }
    navigate(`/customers/${saved.id}`);
  };

  if (isEdit && customerQuery.isPending) return <ListSkeleton rows={6} />;

  if (isEdit && !customer) {
    return (
      <div className="flex flex-col gap-4">
        <BackToCustomer customerId={customerId ?? null} />
        <p className="text-sm text-muted-foreground">{t('customers.notFound')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <BackToCustomer customerId={isEdit ? (customerId ?? null) : null} />

      <PageHeader
        title={isEdit ? t('customers.form.editTitle') : t('customers.form.createTitle')}
        description={
          isEdit ? t('customers.form.editDescription') : t('customers.form.createDescription')
        }
      />

      <CustomerForm
        variant="page"
        customer={customer}
        prefillQuery={prefill}
        measurements={drafts.map((draft) => draft.captured)}
        onSaved={afterSave}
      >
        {/* Everything a customer accumulates, shown only once there is a customer to have
            accumulated it. Read-only surfaces beneath the form rather than fields inside it:
            a measurement is taken with a tape in hand, not typed while correcting an address. */}
        {/* Neither panel is given a heading here — both draw their own, and a card that
            announced "Measurements" above a panel announcing "Saved measurements" would be the
            same word twice with nothing between them. */}
        {customer ? (
          <>
            <section
              className="rounded-lg border border-border bg-card p-4 sm:p-6"
              aria-label={t('customers.detail.tabs.measurements')}
            >
              <MeasurementsPanel owner={{ kind: 'customer', customerId: customer.id }} />
            </section>

            <section
              className="rounded-lg border border-border bg-card p-4 sm:p-6"
              aria-label={t('customers.detail.ordersHeading')}
            >
              <CustomerOrders customerId={customer.id} />
            </section>
          </>
        ) : (
          <section
            className="rounded-lg border border-border bg-card p-4 sm:p-6"
            aria-label={t('customers.detail.tabs.measurements')}
          >
            <DraftMeasurementsPanel drafts={drafts} onChange={setDrafts} />
          </section>
        )}
      </CustomerForm>
    </div>
  );
}

/**
 * Back to wherever this form was opened from — the customer's own page when correcting one, the
 * book when adding one. Not the browser's Back: a form reached after a failed save should still
 * offer a way out that lands somewhere sensible.
 */
function BackToCustomer({ customerId }: { customerId: string | null }): ReactElement {
  const { t } = useTranslation();
  return (
    <Link
      to={customerId ? `/customers/${customerId}` : '/customers'}
      className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
    >
      <ChevronLeft aria-hidden="true" className="size-4" />
      {customerId ? t('customers.form.backToCustomer') : t('customers.backToList')}
    </Link>
  );
}

export default CustomerFormPage;
